import { useEffect, useRef, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// WebSocket close codes that mean "infra cut us off, not user intent" — we
// auto-reconnect on these. Codes carrying our own auth/quota signal (44xx)
// are user-actionable, so we surface them and stop.
const TRANSIENT_CLOSE_CODES = new Set([1000, 1001, 1005, 1006, 1011, 1012, 1013]);
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAYS_MS = [500, 1000, 2000, 4000, 8000];
const PING_INTERVAL_MS = 20000;

const buildWsUrl = (roomId) => {
  const wsProto = API_BASE_URL.startsWith('https') ? 'wss' : 'ws';
  const wsHost = API_BASE_URL.replace(/^https?:\/\//, '');
  const params = new URLSearchParams();
  if (roomId) params.set('room_id', roomId);
  const qs = params.toString();
  return `${wsProto}://${wsHost}/api/terminal/ws${qs ? `?${qs}` : ''}`;
};

// Maps the close-code branches to the user-facing error + xterm.write tag.
// Keeping this as data (vs. an if-ladder) makes the reconnect/auth decision
// surface easy to read.
const FATAL_CLOSE_CODES = {
  4401: { msg: 'Not authenticated. Sign in first.', term: '\r\n\x1b[31mNot authenticated.\x1b[0m\r\n' },
  4402: { msg: 'Pro-only feature. Upgrade to unlock the terminal.', term: null },
  4403: { msg: 'Out of sandbox credits this month. Earn more or upgrade.', term: '\r\n\x1b[33mOut of sandbox credits.\x1b[0m\r\n' },
  4409: { msg: 'You already have an active terminal session in another tab.', term: null },
};

/**
 * Owns the full WebSocket lifecycle for the bash PTY: open → ping loop →
 * reconnect-with-backoff → close on unmount. Intentionally framework-light
 * (just useState + refs) so it stays testable in isolation.
 *
 * Inputs:
 *   - `roomId`: optional room slug, forwarded to the backend so room-specific
 *     sandbox files are seeded.
 *   - `getTerminal`: function returning the live xterm.Terminal instance.
 *     Lazy lookup avoids tying the hook to a specific component layout.
 *
 * Outputs:
 *   - `status` + `errorMsg`: render state for the status bar.
 *   - `sendInput(data)` / `sendResize({cols, rows})`: forward terminal events
 *     to whichever WS is currently open.
 */
export const useTerminalSocket = ({ roomId, getTerminal }) => {
  const wsRef = useRef(null);
  const userClosedRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const pingTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const [status, setStatus] = useState('connecting');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const writeTerm = (text) => {
      const term = getTerminal();
      if (term && text) term.write(text);
    };

    const sendInitialResize = (ws) => {
      const term = getTerminal();
      if (!term) return;
      try {
        ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
      } catch (err) {
        console.debug('[xterm] initial resize send failed:', err?.message);
      }
    };

    const stopPing = () => {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    };

    const startPing = (ws) => {
      // Client→server NUL heartbeat (mirrors server). Server filters it so
      // it doesn't reset the idle-timeout clock.
      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.send('\x00'); } catch (err) {
            console.debug('[xterm] client ping failed:', err?.message);
          }
        }
      }, PING_INTERVAL_MS);
    };

    const handleTransientClose = (code) => {
      // The preview ingress enforces a hard 60s max WS lifetime that no
      // amount of heartbeats can override (verified). On each disconnect
      // with a transient code we silently reconnect — bash state resets
      // per reconnect but typing flow stays continuous.
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = RECONNECT_DELAYS_MS[reconnectAttemptsRef.current] ?? 8000;
        reconnectAttemptsRef.current += 1;
        setStatus('reconnecting');
        writeTerm(
          `\r\n\x1b[33mConnection paused — reconnecting (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})…\x1b[0m\r\n`
        );
        reconnectTimerRef.current = setTimeout(openSocket, delay);
        return;
      }
      setStatus('closed');
      setErrorMsg('Could not reconnect after several attempts. Refresh the page.');
      writeTerm('\r\n\x1b[31mGave up reconnecting. Please refresh.\x1b[0m\r\n');
    };

    function openSocket() {
      const ws = new WebSocket(buildWsUrl(roomId));
      wsRef.current = ws;

      ws.onopen = () => {
        const isReconnect = reconnectAttemptsRef.current > 0;
        if (isReconnect) {
          writeTerm('\r\n\x1b[32m✓ Reconnected — new shell session.\x1b[0m\r\n');
        }
        reconnectAttemptsRef.current = 0;
        setStatus('connected');
        setErrorMsg('');
        sendInitialResize(ws);
      };

      ws.onmessage = (event) => {
        // Heartbeat NUL or empty frames — never write to the terminal.
        if (event.data === '\x00' || event.data === '' || event.data?.length === 0) return;
        writeTerm(event.data);
      };

      ws.onerror = () => setStatus('error');

      ws.onclose = (e) => {
        stopPing();
        if (userClosedRef.current) return; // unmount path — no reconnect

        const fatal = FATAL_CLOSE_CODES[e.code];
        if (fatal) {
          setStatus('closed');
          setErrorMsg(fatal.msg);
          if (fatal.term) writeTerm(fatal.term);
          return;
        }

        if (TRANSIENT_CLOSE_CODES.has(e.code) || e.code === 0) {
          handleTransientClose(e.code);
          return;
        }

        // Unknown code — surface verbatim for triage.
        setStatus('closed');
        console.debug(`[xterm] unhandled close code=${e.code} reason=${e.reason}`);
        writeTerm(`\r\n\x1b[33mSession ended (code ${e.code}).\x1b[0m\r\n`);
      };

      startPing(ws);
    }

    openSocket();

    return () => {
      userClosedRef.current = true;
      stopPing();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      try { wsRef.current?.close(); } catch (err) {
        console.debug('[xterm] ws.close:', err?.message);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendInput = (data) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(data);
  };

  const sendResize = ({ cols, rows }) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'resize', cols, rows }));
    }
  };

  return { status, errorMsg, sendInput, sendResize };
};
